import { useState, useEffect, useCallback } from 'react'
import {
  ScannerProvider,
  ScannerConfig,
  ScannerContextValue,
  ScanResult,
} from './types'
import { ScannerFactory } from './ScannerFactory'
import { useFlags } from 'launchdarkly-react-client-sdk'

export function useScanner(config: ScannerConfig): ScannerContextValue {
  const [scanner, setScanner] = useState<ScannerProvider | null>(null)
  const [isActive, setIsActive] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [currentCamera, setCurrentCamera] = useState<string | undefined>()
  const [currentScan, setCurrentScan] = useState<ScanResult | undefined>()
  const flags = useFlags()
  const useScanbotSDK = flags.useScanbotSdk

  useEffect(() => {
    let mounted = true

    const initScanner = async () => {
      try {
        const newScanner = await ScannerFactory.createScanner(useScanbotSDK)
        await newScanner.init()
        if (mounted) {
          setScanner(newScanner)
        }
      } catch (err) {
        if (mounted) {
          setError(err as Error)
        }
      }
    }

    initScanner()

    return () => {
      mounted = false
      if (scanner) {
        scanner.stop()
      }
    }
  }, [useScanbotSDK])

  const start = useCallback(async () => {
    if (!scanner) return
    try {
      await scanner.start(config)
      setIsActive(true)
    } catch (err) {
      setError(err as Error)
    }
  }, [scanner, config])

  const stop = useCallback(async () => {
    if (!scanner) return
    try {
      await scanner.stop()
      setIsActive(false)
    } catch (err) {
      setError(err as Error)
    }
  }, [scanner])

  // Handle scanner start/stop based on isEnabled
  useEffect(() => {
    if (!scanner) return

    if (config.isEnabled) {
      start()
    } else {
      stop()
    }
  }, [scanner, config.isEnabled, start, stop])

  const changeCamera = useCallback(
    async (deviceId: string) => {
      setCurrentCamera(deviceId)

      if (!scanner) return

      try {
        await scanner.changeCamera?.(deviceId)
      } catch (err) {
        setError(err as Error)
      }
    },
    [scanner]
  )

  return {
    isActive,
    error,
    start,
    stop,
    changeCamera,
    currentCamera,
    currentScan,
    setCurrentScan,
    isEnabled: config.isEnabled,
  }
}
